/* eslint-disable max-len */
/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines */
import {
    AudienceMetadata,
    AudienceMetadataContent,
    AudienceMetadataProps,
    Metadata,
} from './types'

import {
    ChannelMetadataNote,
    NinchatAudienceMetadata,
    QuestionnaireAnswers,
    SsoAddress,
    TagID,
    UserID,
} from 'api/types/properties'

import React, {useEffect} from 'react'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'

import AudienceMetadataView from './AudienceMetadataView'
import {BoostHistoryType} from '../BoostHistory/types'
import {HtaConnectorType} from '../HtaConnector/types'
import {NinchatProperties} from '../../api/types'
import {NinchatState} from 'api/types'

import {PrescriptionRenewalSurveyResultsType} from 'components/PrescriptionRenewalSurveyResults/types'
import {describeFile} from 'api/action/describeFile'
import {isRealmOwnerOrOperator} from 'utils/realm/realm'
import {sortNestedObject} from 'utils/props'
import {useChannel} from 'hooks/ninchat'

const getPreAnswers = (metadata?: NinchatProperties.NinchatAudienceMetadata): QuestionnaireAnswers => (metadata?.pre_answers ? metadata?.pre_answers : {})
const getPostAnswers = (metadata?: NinchatProperties.NinchatAudienceMetadata): QuestionnaireAnswers => (metadata?.post_answers ? metadata?.post_answers : {})
const getNotes = (metadata?: NinchatProperties.NinchatAudienceMetadata): AudienceMetadata => metadata?.Notes?.reduce((acc: AudienceMetadata, note: ChannelMetadataNote) => {
    acc[note.label] = `${note.value ?? ''}`
    return acc
}, {}) ?? {}
const getTagIds = (metadata?: NinchatProperties.NinchatAudienceMetadata): TagID[] => (metadata?.tag_ids ? metadata?.tag_ids : [])
const getBoostHistory = (metadata?: NinchatProperties.NinchatAudienceMetadata): BoostHistoryType => metadata?._Boost_history as unknown as BoostHistoryType
const getHtaConnector = (metadata?: NinchatProperties.NinchatAudienceMetadata): HtaConnectorType => metadata?.secure?._HTA_connector as unknown as HtaConnectorType
const getHtiaConnector = (metadata?: NinchatProperties.NinchatAudienceMetadata): HtaConnectorType => metadata?.secure?._HTIA_connector as unknown as HtaConnectorType
const getPrescriptionRenewalSurveyResults = (
    metadata?: NinchatProperties.NinchatAudienceMetadata,
): PrescriptionRenewalSurveyResultsType | undefined => metadata?.secure?._Prescription_renewal_survey_results as PrescriptionRenewalSurveyResultsType | undefined

const getSso = (metadata?: NinchatProperties.NinchatAudienceMetadata) => {
    const sso = {...metadata?.sso}

    if (sso.Authentication && sso.Authentication !== 'successful') {
        return undefined
    }

    delete sso.Authentication

    // make all nested objects flat
    const ssoMapped = Object.entries(sso)
        .filter(([key]) => key !== 'Delegate') // filter out Delegate, as it is handled separately now
        .map(([key, value]) => {
            if (typeof value === 'object') {
                if (key === 'Vakinainen kotimainen lähiosoite') {
                    return {
                        key,
                        // eslint-disable-next-line no-extra-parens
                        value: `${(value as SsoAddress).Katuosoite}, ${(value as SsoAddress).Postinumero}, ${(value as SsoAddress).Postitoimipaikka}`,
                    }
                }

                return {
                    key,
                    value: Object.values(value).flat()
                        .join(' '),
                }
            }
            return {
                key,
                value,
            }
        })
        .reduce((acc: {[key: string]: string}, cur) => {
            acc[cur.key] = cur.value.toString()
            return acc
        }, {})


    return ssoMapped
}

const getSsoDelegate = (metadata?: NinchatProperties.NinchatAudienceMetadata): Array<AudienceMetadata> => {
    const sso = {...metadata?.sso}
    if (!sso || !('Delegate' in sso)) {
        return []
    }
    const {Delegate: delegate = []} = sso
    return delegate.map((current: AudienceMetadata) => {
        const roles = (current?.Roles ?? []) as Array<string>
        return {
            ...current,
            Roles: roles.join(', '), // Join roles to a string
        }
    })
}

const getMetadata = (metadata: NinchatAudienceMetadata, audienceId?: NinchatProperties.AudienceID, userId?: NinchatProperties.UserID): AudienceMetadata | undefined => {
    const ignoredKeys = [
        '_Boost_history',
        '_HTA_connector',
        '_HTIA_connector',
        '_Prescription_renewal_survey_results',
        'pre_answers',
        'post_answers',
        'shared',
        'sso',
        'tag_ids',
        'Notes',
    ] // Ignored or displayed elsewhere

    const parsedMetadata = JSON.parse(JSON.stringify(metadata, (key, value) => (ignoredKeys.includes(key) ? undefined : value)))
    const _ninchatMetadata = parsedMetadata?.ninchat

    const ninchatAudienceMetadata = {
        ...parsedMetadata,
        ninchat: {
            ...typeof _ninchatMetadata === 'object' && {..._ninchatMetadata},
            ...audienceId && {'Audience ID': audienceId},
            ...userId && {'User ID': userId},
        },
    }

    return ninchatAudienceMetadata
}

const getNinchatMetaData = (metadata : AudienceMetadata | undefined): AudienceMetadata => metadata?.ninchat as AudienceMetadata

const getSecureMetaData = (metadata : AudienceMetadata | undefined): AudienceMetadata => metadata?.secure as AudienceMetadata

const getDeviceOrVarsMetadata = (metadata: AudienceMetadata | undefined, ttype: 'device' | 'vars'): AudienceMetadata | undefined => {
    const currentMetadata = ttype === 'device' ? metadata?.device : metadata?.vars ?? undefined
    if (typeof currentMetadata === 'string') {
        try {
            return JSON.parse(currentMetadata)
        } catch (_) {
            return undefined
        }
    }
    if (currentMetadata) {
        return currentMetadata as AudienceMetadata
    }
    return undefined
}

const getGeneralMetadata = (metadata : AudienceMetadata | undefined): AudienceMetadata | undefined => {
    if (!metadata) {
        return undefined
    }
    return Object.keys(metadata)
        .filter((key) => !['ninchat', 'secure', 'device', 'vars'].includes(key))
        .reduce((cur, key) => Object.assign(cur, {[key]: metadata[key]}), {})
}

// eslint-disable-next-line max-params,max-len
export const getAudienceMetadata = (metadata: Metadata, agentIds: Array<UserID>, ninchat: NinchatState, realmId?: NinchatProperties.RealmID): Array<{meta: AudienceMetadataContent, user_id: UserID}> => {
    const {
        audience_id,
        channel_members_metadata,
        member_message_metadata,
    } = metadata

    const canViewPostAnswers = isRealmOwnerOrOperator(ninchat, realmId)
    const mergedMetadata = {...channel_members_metadata}

    for (const user_id of [...agentIds, ...Object.keys(member_message_metadata ?? {})]) {
        const _metadata = {
            ...mergedMetadata?.[user_id],
            ...member_message_metadata?.[user_id],
        }
        if (user_id) {
            mergedMetadata[user_id] = _metadata
        }
    }

    const ninchatAudienceMetadata = Object.entries(mergedMetadata).map(([user_id, meta]) => ({
        allMetadata: meta,
        parsedMetadata: getMetadata(meta, audience_id, user_id) ?? {},
        user_id,
    }))

    return ninchatAudienceMetadata.map(({
        user_id, parsedMetadata, allMetadata,
    }) => {
        const audienceMetadata = {
            audienceMetadata: getGeneralMetadata(parsedMetadata),
            boostHistory: getBoostHistory(allMetadata),
            device: getDeviceOrVarsMetadata(parsedMetadata, 'device'),
            htaConnector: getHtaConnector(allMetadata),
            htiaConnector: getHtiaConnector(allMetadata),
            ninchat: getNinchatMetaData(parsedMetadata),
            notes: getNotes(allMetadata),
            postAnswers: canViewPostAnswers ? getPostAnswers(allMetadata) : {},
            preAnswers: getPreAnswers(allMetadata),
            prescriptionRenewalSurveyResults: getPrescriptionRenewalSurveyResults(allMetadata),
            secure: getSecureMetaData(parsedMetadata),
            sso: getSso(allMetadata),
            ssoDelegate: getSsoDelegate(allMetadata),
            tagIds: getTagIds(allMetadata),
            vars: getDeviceOrVarsMetadata(parsedMetadata, 'vars'),
        }

        return {
            meta: audienceMetadata,
            user_id,
        }
    })
}

const getChannelAudienceMetadata = (ninchat: NinchatState, agentIds: Array<UserID>, channelId?: string) => {
    if (!channelId) {
        return undefined
    }

    const audienceId = ninchat?.user_channels?.[channelId]?.channel_attrs.audience_id
    const channelMembersMetadata = ninchat?.user_channels?.[channelId]?.channel_members_metadata
    const memberMessageMetadata = ninchat?.user_channels?.[channelId]?.member_message_metadata
    const metadata = {
        audience_id: audienceId,
        channel_members_metadata: channelMembersMetadata,
        member_message_metadata: memberMessageMetadata,
    }
    const realmId = ninchat?.user_channels?.[channelId]?.realm_id

    return getAudienceMetadata(metadata, agentIds, ninchat, realmId)
}

// eslint-disable-next-line complexity
const checkMetadataValues = (attachments: NinchatProperties.NinchatFile[], agentIds: Array<UserID>) => ({
    meta, user_id,
}: ReturnType<typeof getAudienceMetadata>[0]): boolean => {
    const metadataKeys = new Set([
        ...Object.keys(meta?.audienceMetadata ?? {}),
        ...Object.keys(meta?.boostHistory ?? {}),
        ...Object.keys(meta?.htaConnector ?? {}),
        ...Object.keys(meta?.htiaConnector ?? {}),
        ...Object.keys(meta?.secure ?? {}),
        ...Object.keys(meta?.ninchat ?? {}),
        ...Object.keys(meta?.notes ?? {}),
        ...Object.keys(meta?.device ?? {}),
        ...Object.keys(meta?.vars ?? {}),
        ...Object.keys(meta?.postAnswers ?? {}),
        ...Object.keys(meta?.preAnswers ?? {}),
        ...Object.keys(meta?.prescriptionRenewalSurveyResults ?? {}),
        ...Object.keys(meta?.sso ?? {}),
        ...Object.keys(meta?.ssoDelegate ?? []),
        ...Object.keys(meta?.tagIds ?? []),
    ])

    // force displaying pre_answers if there are attachments
    if (attachments.length > 0 && !metadataKeys.has('pre_answers') && !agentIds.includes(user_id)) {
        metadataKeys.add('pre_answers')
    }

    // if there is no metadata, then return false
    if (metadataKeys.size === 0) {
        return false
    }

    // if keys only contains 'Audience ID', and 'User ID', then return false
    if (metadataKeys.size === 2 && metadataKeys.has('Audience ID') && metadataKeys.has('User ID')) {
        return false
    }

    return true
}

const sortMetadata = ({
    meta, user_id,
}: ReturnType<typeof getAudienceMetadata>[0]) => ({
    meta: sortNestedObject(meta),
    user_id,
})

const AudienceMetadataComponent = (props: AudienceMetadataProps): React.ReactElement => {
    const {
        agentIds,
        audienceFiles,
        channelId,
        compact,
        exclude,
        lite,
        keepHeader,
        metadata,
        openModal,
        realmId,
        user_id,
        showOnlyOneUserId,
    } = props


    const viewProps = {
        compact,
        exclude,
        keepHeader,
        lite,
        openModal,
        realmId,
        user_id,
    }
    const fetchedAudienceMetadata = useAppSelector(({ninchat}) => (metadata ? getAudienceMetadata(metadata, agentIds, ninchat, realmId) : getChannelAudienceMetadata(ninchat, agentIds, channelId)))
    const currentChannel = useChannel(channelId || '')
    const dispatch = useAppDispatch()

    const [attachments, setAttachments] = React.useState<NinchatProperties.NinchatFile[]>([])

    const attachmentsFromStore = Object.entries(audienceFiles ?? currentChannel.audience_files ?? []).map(([fileId, file]) => ({
        file_attrs: file.file_attrs,
        file_id: fileId,
    })) as NinchatProperties.NinchatFile[]

    const prepareAttachments = () => Promise.all(attachmentsFromStore.map(async (file) => {
        let fileDetails: Partial<NinchatProperties.NinchatFile> = {}
        const fileId = file.file_id

        if (fileId) {
            try {
                fileDetails = await dispatch(describeFile({file_id: fileId})).unwrap()
            } catch (error) {
                console.error('Error describing file:', error)
            }
        }

        return {
            file_id: fileId || '',
            file_attrs: file.file_attrs,
            converted_url: fileDetails.converted_url || '',
            file_url: fileDetails.file_url || '',
            thumbnail_url: fileDetails.thumbnail_url || '',
            url_expiry: fileDetails.url_expiry || 0,
        }
    }))

    useEffect(() => {
        if (attachmentsFromStore.length > 0) {
            prepareAttachments().then((filesDescribed) => {
                setAttachments(filesDescribed)
            })
        }
    }, [attachmentsFromStore.length])

    return (
        <div>
            {
                fetchedAudienceMetadata
                    ?.filter(checkMetadataValues(attachments, agentIds))
                    ?.map(sortMetadata)
                    .map(({
                        meta, user_id: userId,
                    }) => {
                        if (showOnlyOneUserId && userId !== showOnlyOneUserId) {
                            return null
                        }
                        return <AudienceMetadataView attachments={attachments} key={userId} {...viewProps} {...meta} user_id={userId} />
                    })
            }
        </div>
    )
}

AudienceMetadataComponent.defaultProps = {
    channelId: null,
    compact: false,
    exclude: [],
    lite: false,
    metadata: null,
    realmId: null,
}

export default AudienceMetadataComponent
